import { AlertMessageProps } from "@hqy/react-shared-components";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { Dispatch, SetStateAction, useState } from "react";
import UserCredentialsApi from "../../services/Api";
import { defaultLimit } from "../../services/Constatnts";
import {
  Brand,
  ChangeLogResponse,
  CredentialResponse,
  freeFormQuestionId,
  Partition,
  PendingActions,
  PredefinedQuestion,
  QuestionAnswer,
  UserActivityResponse,
  UserPermissions,
} from "../../services/Types";
import ChangeLogTable from "../ChangeLogTable";
import LoginAndSecurity from "../LoginAndSecurity/LoginAndSecurity";
import PermissionCheck from "../PermissionCheck";
import UserActivityTable from "../UserActivityTable/UserActivityTable";

const useStyles = makeStyles({
  tabsHolder: {
    backgroundColor: "#fff7e8",
    padding: "11px 15px 64px",
    width: "1200px",

    "& h3": {
      color: "#595959",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
});

const LoginSupportTabs = ({
  userCredentials,
  userPermissions,
  predefinedQuestions,
  userCredentialsPendingActions,
  setUserCredentials,
  setUserCredentialsPendingActions,
  userActivity,
  setUserActivity,
  changeLogs,
  setChangeLogs,
  activeUserActivityPage,
  setActiveUserActivityPage,
  activeChangeLogPage,
  setActiveChangeLogPage,
  handleTabDataReload,
  brand,
  userCredentialsApi,
  activeAnswer,
  setActiveAnswer,
  setAlertMessageProps,
  errorOutlines,
  setErrorOutlines
}: {
  userCredentials: CredentialResponse;
  userPermissions: Array<UserPermissions>;
  predefinedQuestions: Array<PredefinedQuestion>;
  userCredentialsPendingActions: PendingActions;
  setUserCredentials: Dispatch<SetStateAction<CredentialResponse | null>>;
  setUserCredentialsPendingActions: Dispatch<SetStateAction<PendingActions>>;
  userActivity: UserActivityResponse;
  setUserActivity: Dispatch<SetStateAction<UserActivityResponse>>;
  changeLogs: ChangeLogResponse;
  setChangeLogs: Dispatch<SetStateAction<ChangeLogResponse>>;
  activeUserActivityPage: number;
  setActiveUserActivityPage: Dispatch<SetStateAction<number>>;
  activeChangeLogPage: number;
  setActiveChangeLogPage: Dispatch<SetStateAction<number>>;
  handleTabDataReload: any;
  brand?: Brand;
  userCredentialsApi: UserCredentialsApi;
  activeAnswer: string;
  setActiveAnswer: Dispatch<SetStateAction<string>>;
  setAlertMessageProps: Dispatch<SetStateAction<AlertMessageProps | null>>;
  errorOutlines: any;
  setErrorOutlines: Dispatch<SetStateAction<any>>;
}) => {
  const classes = useStyles();
  const isOktaUser = userCredentials?.partition === Partition.OKTA;
  
  const [openedTab, setOpenedTab] = useState<string>(!isOktaUser ? "1" : "3");

  const handleTabChange = (event: any, newValue: string) => {
    setOpenedTab(newValue);
  };

  const handleUserActivityPageChange = (value: number) => {
    if (userCredentials) {
      setActiveUserActivityPage(value);
      userCredentialsApi
        .getUserActivity(userCredentials?.id, (value - 1) * defaultLimit)
        .then((resp) => {
          setUserActivity(resp);
        });
    }
  };

  const handleChangeLogsPageChange = (value: number) => {
    if (userCredentials) {
      setActiveChangeLogPage(value);
      userCredentialsApi.getChangeLogs(userCredentials.id, (value-1) * defaultLimit).then((resp)=>{
        setChangeLogs(resp);
      });
    }
  }

  let allQuestions = [...predefinedQuestions];
  const activeUserQuestion = userCredentials?.questions?.[0];
  const [activeUserQuestionId, setActiveUserQuestionId] = useState<
    string | undefined
  >(userCredentials?.questions?.[0]?.id);

  const selectedQuestion = {
    id:
      activeUserQuestion?.questionId ||
      (activeUserQuestion?.question && freeFormQuestionId) ||
      "",
    question: activeUserQuestion?.question || "",
  };

  if (selectedQuestion?.id === freeFormQuestionId) {
    allQuestions = [...predefinedQuestions, selectedQuestion];
  }

  const [selectedQuestionId, setSelectedQuestionId] = useState<string>(selectedQuestion?.id);

  return (
    <PermissionCheck
      userPermissions={userPermissions}
      permissionToCheck={UserPermissions.ADMIN_SECTION_VIEW}
    >
      <div className={classes.tabsHolder} data-testid="loggin-support-tabs">
        <TabContext value={openedTab}>
          <div>
            <TabList onChange={handleTabChange} aria-label="lab API tabs example">
              {
                !isOktaUser &&
                <Tab label="Login & Security" value="1" tabIndex={0}/>
              }
              {
                !isOktaUser &&
                <Tab label="User Activity (30 days)" value="2" tabIndex={0}/> 
              }
              <Tab label="Change Tracking (90 days)" value="3" tabIndex={0}/>
            </TabList>
          </div>
          {
            !isOktaUser &&
            <>
              <TabPanel value="1">
                <LoginAndSecurity
                  {...{
                    userCredentials,
                    userPermissions,
                    allQuestions,
                    selectedQuestion,
                    activeUserQuestionId,
                    setActiveUserQuestionId,
                    userCredentialsPendingActions,
                    setUserCredentials,
                    userCredentialsApi,
                    setUserCredentialsPendingActions,
                    handleTabDataReload,
                    brand,
                    activeAnswer,
                    setActiveAnswer,
                    selectedQuestionId,
                    setSelectedQuestionId,
                    setAlertMessageProps,
                    errorOutlines,
                    setErrorOutlines
                  }}
                />
              </TabPanel>
              <TabPanel value="2">
                <UserActivityTable
                  {...{
                    userCredentials,
                    userActivity,
                    handleUserActivityPageChange,
                    activeUserActivityPage,
                  }}
                />
              </TabPanel>
            </>
          }
          <TabPanel value="3">
            <ChangeLogTable
              {...{
                userCredentials,
                changeLogs,
                handleChangeLogsPageChange,
                activeChangeLogPage,
              }}
            />
          </TabPanel>
        </TabContext>
      </div>
    </PermissionCheck>
  );
};

export default LoginSupportTabs;
